import React, { useEffect, useMemo, useReducer } from 'react';

// helpers
import { cn } from '../../../../../helper/cn';

// redux
import {
  INITIAL_STATE,
  templateReducer,
} from '../../../../channels/reducers/templateReducer';

// components
import MediaMessage from '../preview/MediaMessage';
import Template from '../preview/Template';
import TemplatePreview from '../../../../../components/commonComponents/templates/TemplatePreview';
import QrTriggerPreview from '../preview/triggers/QrTriggerPreview';
import ApiTriggerPreview from '../preview/triggers/ApiTriggerPreview';
import KeywordTriggerPreview from '../preview/triggers/KeywordTriggerPreview';
import MetaPreview from '../preview/MetaPreview';
import OperationsPreview from '../preview/operations';

// constants
import { ACTIONS_LIST, OPERATIONS_LIST } from '../../../constants/triggers';

const MESSAGE_TYPES = [
  'TEXT',
  'MEDIA',
  'BUTTON',
  'LIST',
  'LOCATION_REQUEST_MESSAGE',
  'FLOW',
  'CTA_URL',
  'CONTACTS',
];

const NodeDetails = ({
  node,
  newEdge,
  color,
  isConnected,
  onEdgeMouseDown,
  checkIsEdgeConnected,
  allowToEditFlow,
  nodeCurrentStatus,
  allowToShowEdge,
  previewMode,
  previewType = 'default',
  findEmptySpace,
  onChange,
  closeNode,
}) => {
  const [templateData, templateDispatch] = useReducer(
    templateReducer,
    INITIAL_STATE
  );

  const actionTypes = ACTIONS_LIST.map(item => item?.key);
  const operationTypes = OPERATIONS_LIST.map(item => item?.key);

  // ---- derived flags/values -------------------------------------------------
  const isTrigger = node?.type === 'TRIGGER';
  const triggerType = node?.trigger_type;
  const isTemplateNode = node?.data?.type === 'template';
  const isLastNode = !!node?.is_last_node;

  const effectiveAllowToShowEdge = isLastNode ? false : allowToShowEdge;

  const containerWidthClass = useMemo(() => {
    if (previewMode) return 'w-full';
  
    // Define custom width rules based on conditions
    const widthConfig = [
      {
        condition: isTrigger && ['API_TRIGGER', 'KEYWORD_TRIGGER'].includes(triggerType),
        className: 'min-w-[320px] max-w-[320px]',
      },
      {
        condition: operationTypes.includes(node?.type),
        className: 'min-w-[320px] max-w-[370px]',
      }
    ];
  
    // Find first matching config
    const matchedConfig = widthConfig.find(config => config.condition);
  
    // Fallback if nothing matched
    return matchedConfig?.className || 'min-w-[270px] max-w-[270px]';
  
  }, [isTrigger, triggerType, previewMode, node?.type, operationTypes]);

  const mediaCardWidthClass = previewMode
    ? 'min-w-[300px] max-w-[300px]'
    : 'min-w-[270px] max-w-[270px]';

  // Shared props for edge-aware components
  const sharedEdgeProps = {
    onEdgeMouseDown,
    checkIsEdgeConnected,
    allowToEditFlow,
    allowToShowEdge: effectiveAllowToShowEdge,
    nodeCurrentStatus,
  };

  // ---- effects --------------------------------------------------------------
  useEffect(() => {
    templateDispatch({
      type: 'update_template',
      data: {
        category: node?.data?.category,
        subcategory_type: node?.data?.subcategory_type,
        namespace: node?.data?.template_name,
        components: node?.data?.template,
        name: node?.data?.template_name,
        template_id: node?.data?.template_id,
        message_body: node?.data?.message_body,
      },
    });
  }, [node]);

  // ---- render helpers -------------------------------------------------------
  const renderTrigger = () => {
    if (!isTrigger || previewMode) return null;

    if (triggerType === 'API_TRIGGER') {
      return (
        <ApiTriggerPreview
          node={node}
          color={color}
          isConnected={isConnected}
          {...sharedEdgeProps}
        />
      );
    }

    if (triggerType === 'KEYWORD_TRIGGER') {
      return (
        <KeywordTriggerPreview
          node={node}
          color={color}
          onEdgeMouseDown={onEdgeMouseDown}
          allowToEditFlow={allowToEditFlow}
          allowToShowEdge={effectiveAllowToShowEdge}
        />
      );
    }

    if (triggerType === 'CAMP_QR_URL') {
      return (
        <QrTriggerPreview
          node={node}
          color={color}
          isConnected={isConnected}
          {...sharedEdgeProps}
        />
      );
    }

    if (triggerType === 'META_AD') {
      // when trigger_type === "META_AD". Preserving that behavior below in main render.
      return null;
    }

    return null;
  };

  const renderTemplate = () => {
    if (!isTemplateNode) return null;

    return (
      <>
        <Template
          data={node?.data}
          color={color}
          category={node?.data?.category}
          categoryType={node?.data?.subcategory_type || node?.data?.sub_category}
          node={node}
          btnClassName={cn('border border-[#E8E8EA]')}
          customBtnStyle={newEdge ? 'cursor-grabbing' : 'cursor-default'}
          btnIcon={false}
          rootClassName={cn(
            'min-w-full',
            newEdge ? 'cursor-grabbing' : 'cursor-default'
          )}
          {...sharedEdgeProps}
          previewType={previewType}
          previewMode={previewMode}
        />
      </>
    );
  };

  const renderMessage = () => {
    if (!MESSAGE_TYPES.includes(node?.type)) return null;

    return (
      <div className={cn('text-black', mediaCardWidthClass)}>
        <MediaMessage
          previewMode={previewMode}
          previewType={previewType}
          node={node}
          color={color}
          onEdgeMouseDown={onEdgeMouseDown}
          checkIsEdgeConnected={checkIsEdgeConnected}
          allowToEditFlow={allowToEditFlow}
          allowToShowEdge={effectiveAllowToShowEdge}
          nodeCurrentStatus={nodeCurrentStatus}
          customBodyClassName={newEdge ? 'cursor-grabbing' : 'cursor-default'}
          customFooterClassName={newEdge ? 'cursor-grabbing' : 'cursor-default'}
          rootClassName="p-2.5 shadow-[0px_4px_30px_0px_#2D30361A]"
        />
      </div>
    );
  };

  const renderOperations = () => {
    return (
      <OperationsPreview
        node={node}
        color={color}
        isConnected={isConnected}
        onEdgeMouseDown={onEdgeMouseDown}
        allowToEditFlow={allowToEditFlow}
        allowToShowEdge={effectiveAllowToShowEdge}
        nodeCurrentStatus={nodeCurrentStatus}
        findEmptySpace={findEmptySpace}
        onChange={onChange}
        closeNode={closeNode}
      />
    );
  };

  // ---- render ---------------------------------------------------------------
  return (
    <div className={cn('text-black z-0', containerWidthClass)}>
      {/* Triggers (API / KEYWORD / QR) when not in previewMode */}
      {renderTrigger()}

      {/* Templates */}
      {renderTemplate()}

      {/* Message types */}
      {!isTrigger && !isTemplateNode && renderMessage()}

      {/* Meta Ad trigger (matches original final else case) */}
      {isTrigger && triggerType === 'META_AD' && (
        <MetaPreview
          node={node}
          color={color}
          isConnected={isConnected}
          {...sharedEdgeProps}
        />
      )}

      {/* render operations */}
      {operationTypes.includes(node?.type) && renderOperations()}
    </div>
  );
};

export default NodeDetails;
